import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ClientWaitingPill } from "../components/notes/ClientWaiting";
import { useClientWaiting } from "../hooks/useClientWaiting";
import { emergencySla } from "../lib/emergencySla";
import { SlaLadderBeacon, SlaTierBadge, SlaTimer } from "../components/EmergencySla";
import API from "../lib/api";
import { CardListSkeleton } from "../components/Skeletons";
import AccessNotesField from "../components/AccessNotesField";
import ClientPropertyPicker from "../components/ClientPropertyPicker";
import ClientDot, { ClientFilterChip } from "../components/ClientDot";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { FileDropZone } from "../components/FileDropZone";
import { NoticeEditor, EMPTY_NOTICE } from "../components/NoticeEditor";
import { JobFormsPicker } from "../components/JobFormsPanel";
import { AiBriefButton, AiSmartAssignButton } from "../components/AiFeatures";
import { Lightning, Plus, MagnifyingGlass, MapPin, Wrench, User, Phone, Envelope, CaretLeft, CaretRight, ChartBar, Bookmark, ListChecks, HardHat, MapPinLine } from "@phosphor-icons/react";
import RamsFlag from "../components/hs/RamsFlag";
import ScopeOfWorksField from "../components/jobs/ScopeOfWorksField";
import { toast } from "sonner";
import AddressAutocomplete from "../components/AddressAutocomplete";
import QuickCompleteButton from "../components/QuickCompleteButton";
import ClientFilterSelect, { matchesClientFilter, clientFilterFromParams, applyClientFilterParam, clientLabel } from "../components/ClientFilterSelect";
import { useAuth } from "../contexts/AuthContext";

const URGENCY = {
  critical: { label: "Critical", hours: 4, color: "bg-red-600 text-white", badge: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", ring: "ring-red-500/30" },
  urgent: { label: "Urgent", hours: 8, color: "bg-orange-500 text-white", badge: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300", ring: "ring-orange-500/30" },
  moderate: { label: "Moderate", hours: 12, color: "bg-amber-500 text-white", badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", ring: "ring-amber-500/30" },
  minor: { label: "Minor", hours: 24, color: "bg-blue-500 text-white", badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", ring: "ring-blue-500/30" },
};

const STATUS_MAP = {
  open: { label: "Open", color: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
  acknowledged: { label: "Acknowledged", color: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400" },
};

const PAGE_SIZE = 15;

export default function EmergencyJobsPage() {
  const { byId: waitingById } = useClientWaiting();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canComplete = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [urgencyFilter, setUrgencyFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [operatives, setOperatives] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientMode, setClientMode] = useState("new"); // "existing" | "new"
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [workTemplates, setWorkTemplates] = useState([]);
  const vendorSearchRef = useRef(null);
  const [page, setPage] = useState(1);
  const [, setTick] = useState(0);
  const timerRef = useRef(null);

  const [form, setForm] = useState({
    title: "", description: "", urgency: "urgent",
    client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "",
    requester_contact_id: "", requester_contact_name: "", requester_contact_color: "",
    property_name: "", street_address: "", town_city: "", county: "", postcode: "", what3words: "",
    site_contact_name: "", site_contact_phone: "", site_contact_email: "",
    category: "general", assigned_vendor_id: "", assigned_vendor_name: "",
    assigned_operative_id: "", assigned_operative_name: "",
    instructions: "", scope_of_works: "", attachments: [], job_form_ids: [], notice: { ...EMPTY_NOTICE },
  });
  const [selectedClientContacts, setSelectedClientContacts] = useState([]);

  const fetchJobs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (urgencyFilter !== "all") params.set("urgency", urgencyFilter);
      const { data } = await API.get(`/emergency-jobs?${params}`);
      setJobs(data.jobs);
      setTotal(data.total);
    } catch { toast.error("Failed to load emergency jobs"); }
    finally { setLoading(false); }
  }, [statusFilter, urgencyFilter]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Auto-open create dialog when arriving with ?new=1
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setShowCreate(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  useEffect(() => {
    API.get("/suppliers?limit=500").then(({ data }) => setVendors(Array.isArray(data) ? data : data.vendors || [])).catch(() => {});
    API.get("/in-house/team").then(({ data }) => setOperatives((data.team || []).filter(u => u.active !== false))).catch(() => {});
    API.get("/clients?limit=500").then(({ data }) => setClients(Array.isArray(data) ? data : data.clients || [])).catch(() => {});
    API.get("/work-templates?template_type=emergency").then(({ data }) => setWorkTemplates(data.templates || [])).catch(() => {});
  }, []);

  const applyTemplate = (templateId) => {
    if (!templateId || templateId === "__none__") return;
    const tpl = workTemplates.find(t => t.id === templateId);
    if (!tpl || !tpl.fields) return;
    setForm(prev => ({ ...prev, ...tpl.fields }));
    toast.success(`Template "${tpl.name}" applied`);
  };

  // Live timer: tick every second for active jobs
  useEffect(() => {
    timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Close supplier dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (vendorSearchRef.current && !vendorSearchRef.current.contains(e.target)) {
        setVendorDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { setPage(1); }, [search, statusFilter, urgencyFilter]);

  const [clientFilter, setClientFilterState] = useState(() => clientFilterFromParams(searchParams));
  const setClientFilter = (v) => { setClientFilterState(v); setSearchParams(applyClientFilterParam(searchParams, v), { replace: true }); };
  const [missingScope, setMissingScope] = useState(false);
  const [noRams, setNoRams] = useState(false);
  const needsScope = (j) => !(j.scope_of_works || "").trim() && !["completed", "cancelled"].includes(j.status);
  const missingScopeCount = useMemo(() => jobs.filter(needsScope).length, [jobs]);
  const noRamsCount = useMemo(() => jobs.filter(j => j.rams_status === "missing").length, [jobs]);

  const filtered = useMemo(() => {
    let base = clientFilter === "all" ? jobs : jobs.filter(j => matchesClientFilter(j, clientFilter));
    if (missingScope) base = base.filter(needsScope);
    if (noRams) base = base.filter(j => j.rams_status === "missing");
    if (!search) return base;
    const s = search.toLowerCase();
    return base.filter(j =>
      j.title?.toLowerCase().includes(s) || j.reference?.toLowerCase().includes(s) ||
      j.client_name?.toLowerCase().includes(s) || j.street_address?.toLowerCase().includes(s) ||
      j.assigned_vendor_name?.toLowerCase().includes(s) ||
      j.assigned_operative_name?.toLowerCase().includes(s)
    );
  }, [jobs, search, clientFilter, missingScope, noRams]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await API.post("/emergency-jobs", form);
      toast.success(`Emergency job ${data.reference} created`);
      setShowCreate(false);
      setForm({ title: "", description: "", urgency: "urgent", client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "", property_name: "", street_address: "", town_city: "", county: "", postcode: "", what3words: "", site_contact_name: "", site_contact_phone: "", site_contact_email: "", access_notes: "", category: "general", assigned_vendor_id: "", assigned_vendor_name: "", assigned_operative_id: "", assigned_operative_name: "", instructions: "", scope_of_works: "", attachments: [], job_form_ids: [], notice: { ...EMPTY_NOTICE } });
      setClientMode("new");
      setSelectedClientContacts([]);
      setVendorSearch("");
      navigate(`/emergency-jobs/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create"); }
    finally { setCreating(false); }
  };

  const selectClient = (clientId) => {
    if (!clientId) {
      setForm(f => ({ ...f, client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "" }));
      setSelectedClientContacts([]);
      return;
    }
    const c = clients.find(x => x.id === clientId);
    if (!c) return;
    setForm(f => ({
      ...f,
      client_id: c.id,
      client_name: [c.first_name, c.last_name].filter(Boolean).join(" "),
      client_email: c.email || "",
      client_phone: c.phone || "",
      client_company: c.company_name || "",
      requester_contact_id: "",
      requester_contact_name: "",
      requester_contact_color: "",
    }));
    setSelectedClientContacts(c.contacts || []);
  };

  const selectRequester = (contactId) => {
    if (!contactId || contactId === "__none__") {
      setForm(f => ({ ...f, requester_contact_id: "", requester_contact_name: "", requester_contact_color: "" }));
      return;
    }
    const contact = selectedClientContacts.find(c => c.id === contactId);
    if (contact) {
      setForm(f => ({ ...f, requester_contact_id: contact.id, requester_contact_name: `${contact.first_name} ${contact.last_name}`, requester_contact_color: contact.color || "#3b82f6" }));
    }
  };

  // Smart supplier search: supports "plumber in plymouth", "plumber plymouth", company name, trade, area
  const filteredVendors = useMemo(() => {
    if (!vendorSearch.trim()) return vendors;
    const raw = vendorSearch.toLowerCase().trim();
    // Parse "X in Y" pattern
    const inMatch = raw.match(/^(.+?)\s+in\s+(.+)$/i);
    let tradeQuery = "", areaQuery = "";
    if (inMatch) {
      tradeQuery = inMatch[1].trim();
      areaQuery = inMatch[2].trim();
    } else {
      // Try splitting into words and match across all fields
      tradeQuery = raw;
      areaQuery = raw;
    }

    return vendors.filter(v => {
      const name = (v.company_name || v.name || "").toLowerCase();
      const trade = (v.trade || "").toLowerCase();
      const sectors = Array.isArray(v.sectors) ? v.sectors.join(" ").toLowerCase() : "";
      const areas = Array.isArray(v.areas_covered) ? v.areas_covered.join(" ").toLowerCase() : typeof v.areas_covered === "string" ? v.areas_covered.toLowerCase() : "";

      if (inMatch) {
        const tradeMatch = name.includes(tradeQuery) || trade.includes(tradeQuery) || sectors.includes(tradeQuery);
        const areaMatch = areas.includes(areaQuery);
        return tradeMatch && areaMatch;
      }

      // General search: match any word across any field
      const words = raw.split(/\s+/);
      const all = `${name} ${trade} ${sectors} ${areas}`;
      return words.every(w => all.includes(w));
    });
  }, [vendors, vendorSearch]);

  const selectVendor = (v) => {
    setForm(f => ({ ...f, assigned_vendor_id: v.id, assigned_vendor_name: v.company_name || v.name || "" }));
    setVendorDropdownOpen(false);
    setVendorSearch("");
  };

  const clearVendor = () => {
    setForm(f => ({ ...f, assigned_vendor_id: "", assigned_vendor_name: "" }));
    setVendorSearch("");
  };

  // Summary counts
  const activeJobs = jobs.filter(j => !["completed", "cancelled"].includes(j.status));
  const criticalCount = activeJobs.filter(j => j.urgency === "critical").length;
  const overdueCount = activeJobs.filter(j => emergencySla(j).tier === "breached").length;
  const escalatedCount = activeJobs.filter(j => ["amber", "red"].includes(emergencySla(j).tier)).length;

  return (
    <div data-testid="emergency-jobs-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-5">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-red-600 flex items-center justify-center">
            <Lightning size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Emergency Jobs</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{total} total &middot; {activeJobs.length} active{criticalCount > 0 ? ` · ${criticalCount} critical` : ""}{escalatedCount > 0 ? ` · ${escalatedCount} escalating` : ""}{overdueCount > 0 ? ` · ${overdueCount} over SLA` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/emergency-jobs/analytics">
            <Button variant="outline" size="sm" data-testid="emg-analytics-btn"><ChartBar size={14} className="mr-1" /> Analytics</Button>
          </Link>
          <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setShowCreate(true)} data-testid="create-emergency-btn">
            <Lightning size={16} className="mr-1" weight="fill" /> New Emergency Job
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {Object.entries(URGENCY).map(([key, u]) => {
          const count = activeJobs.filter(j => j.urgency === key).length;
          return (
            <div key={key} className={`border border-border rounded-md p-3 bg-card ring-2 ${count > 0 ? u.ring : "ring-transparent"}`} data-testid={`emg-stat-${key}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${u.color}`}>{u.label}</span>
                <span className="text-[10px] text-muted-foreground">{u.hours}h SLA</span>
              </div>
              <p className="text-xl font-bold font-[Chivo] mt-1">{count}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Active</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search emergency jobs..." className="pl-8 h-8 text-xs" data-testid="emg-search" />
        </div>
        <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
          <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="emg-urgency-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Urgency</SelectItem>
            {Object.entries(URGENCY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="emg-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <ClientFilterSelect rows={jobs} value={clientFilter} onChange={setClientFilter} className="w-[200px] h-8 text-xs" testid="emg-client-filter" />
        {clientFilter.startsWith("id:") && <ClientFilterChip clientId={clientFilter.slice(3)} name={clientLabel(jobs.find((j) => j.client_id === clientFilter.slice(3)))} onClear={() => setClientFilter("all")} testid="emg-client-chip" />}
        <button
          onClick={() => { setMissingScope(v => !v); setPage(1); }}
          className={`flex items-center gap-1 px-2.5 h-8 rounded-md border text-xs font-medium transition-colors ${missingScope ? "border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" : "border-border text-muted-foreground hover:border-amber-400/60"}`}
          title="Open emergency jobs that still need a scope of works"
          data-testid="emg-missing-scope-filter"
        >
          <ListChecks size={13} /> Missing scope{missingScopeCount ? <span className="tabular-nums" data-testid="emg-missing-scope-count">({missingScopeCount})</span> : null}
        </button>
        <button
          onClick={() => { setNoRams(v => !v); setPage(1); }}
          className={`flex items-center gap-1 px-2.5 h-8 rounded-md border text-xs font-medium transition-colors ${noRams ? "border-red-400 bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-200" : "border-border text-muted-foreground hover:border-red-400/60"}`}
          title="Dispatched emergency jobs with no RAMS"
          data-testid="emg-no-rams-filter"
        >
          <HardHat size={13} /> No RAMS{noRamsCount ? <span className="tabular-nums" data-testid="emg-no-rams-count">({noRamsCount})</span> : null}
        </button>
      </div>

      {/* Job Cards */}
      {loading ? (
        <CardListSkeleton rows={6} showHeader={false} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <Lightning size={36} className="mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground mb-3">No emergency jobs found</p>
          <Button variant="outline" size="sm" onClick={() => setShowCreate(true)}>Create one now</Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="emg-jobs-list">
          {paginated.map(j => {
            const u = URGENCY[j.urgency] || URGENCY.urgent;
            const st = STATUS_MAP[j.status] || STATUS_MAP.open;
            const sla = emergencySla(j);
            const addr = [j.street_address, j.town_city, j.postcode].filter(Boolean).join(", ");

            return (
              <div key={j.id}
                className={`border rounded-lg p-4 bg-card cursor-pointer hover:bg-muted/20 transition-colors ${sla.ui.box}`}
                onClick={() => navigate(`/emergency-jobs/${j.id}`)}
                data-testid={`emg-job-${j.id}`}
                data-sla-tier={sla.tier}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <SlaLadderBeacon sla={sla} />
                      <span className="text-[10px] font-mono text-muted-foreground">{j.reference}</span>
                      <ClientWaitingPill item={waitingById[j.id]} testId={`emg-client-waiting-${j.id}`} />
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${u.color}`}>{u.label}{j.response_hours && j.response_hours !== u.hours ? ` · ${j.response_hours}h SLA` : ""}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.color}`}>{st.label}</span>
                      {j.source === "email" && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300" title={j.source_email_from ? `Raised from an email from ${j.source_email_from}` : "Raised from an email"} data-testid={`emg-from-email-${j.id}`}><Envelope size={10} /> From email</span>}
                      {j.address_unconfirmed && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold bg-red-600 text-white" data-testid={`emg-address-tbc-${j.id}`}><MapPinLine size={10} weight="fill" /> Address TBC</span>}
                      <SlaTierBadge sla={sla} testId={`emg-sla-badge-${j.id}`} />
                      <RamsFlag status={j.rams_status} ramsId={j.rams_id} testId={`emg-rams-flag-${j.id}`} />
                      {canComplete && <QuickCompleteButton entity="emergency" id={j.id} reference={j.reference} status={j.status} onDone={fetchJobs} testId={`emg-complete-${j.id}`} clientPoNumber={j.client_po_number} poRequired={!!j.po_required} />}
                    </div>
                    <p className="text-sm font-semibold">{j.title}</p>
                    {addr && <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1"><MapPin size={10} /> {addr}</p>}
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      {j.assigned_vendor_name && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Wrench size={10} /> {j.assigned_vendor_name}</span>
                      )}
                      {j.assigned_operative_name && (
                        <span className="text-[10px] text-primary flex items-center gap-1" data-testid={`emg-operative-chip-${j.id}`}><User size={10} /> {j.assigned_operative_name}</span>
                      )}
                      {j.client_name && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1"><ClientDot clientId={j.client_id} name={j.client_company || j.client_name} size={7} active={clientFilter === `id:${j.client_id}`} onClick={(id) => setClientFilter(clientFilter === `id:${id}` ? "all" : `id:${id}`)} /><User size={10} /> {j.client_name}</span>
                      )}
                      {j.created_by_name && (
                        <span className="text-[10px] text-muted-foreground" data-testid={`emg-added-by-${j.id}`}>Added by {j.created_by_name}</span>
                      )}
                    </div>
                  </div>

                  {/* Live Timer — freezes when the crew arrives */}
                  <div className="text-right shrink-0 min-w-[110px]">
                    <SlaTimer sla={sla} status={j.status} testId={`emg-timer-${j.id}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-[10px] text-muted-foreground">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><CaretLeft size={12} /></Button>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><CaretRight size={12} /></Button>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="create-emergency-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo] flex items-center gap-2"><Lightning size={20} className="text-red-600" weight="fill" /> New Emergency Job</DialogTitle>
            <DialogDescription>Log an emergency job with urgency level and SLA response time.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {workTemplates.length > 0 && (
              <div className="flex items-center gap-2 p-2.5 bg-muted/30 rounded-md border border-dashed border-border" data-testid="emg-template-selector">
                <Bookmark size={14} className="text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Apply Template:</span>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="emg-apply-template"><SelectValue placeholder="Select a template to pre-fill..." /></SelectTrigger>
                  <SelectContent>
                    {workTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Urgency Selector */}
            <div>
              <Label className="text-xs font-semibold">Urgency Level *</Label>
              <div className="grid grid-cols-4 gap-2 mt-2" data-testid="urgency-selector">
                {Object.entries(URGENCY).map(([key, u]) => (
                  <button key={key} type="button"
                    className={`p-3 rounded-lg border-2 text-center transition-all ${form.urgency === key ? `${u.color} border-transparent shadow-md scale-[1.02]` : "border-border bg-card hover:bg-muted/30"}`}
                    onClick={() => setForm({ ...form, urgency: key })}
                    data-testid={`urgency-${key}`}
                  >
                    <p className={`text-xs font-bold ${form.urgency === key ? "" : ""}`}>{u.label}</p>
                    <p className={`text-[10px] mt-0.5 ${form.urgency === key ? "opacity-90" : "text-muted-foreground"}`}>{u.hours}h response</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label>Job Title *</Label>
              <AiBriefButton
                workType="emergency"
                category={form.category}
                propertyAddress={form.property_address}
                onApply={(brief) => setForm(prev => ({
                  ...prev,
                  title: brief.title || prev.title,
                  description: brief.description || prev.description,
                  category: brief.suggested_category || prev.category,
                }))}
              />
            </div>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="mt-1" placeholder="e.g. Burst Pipe - Ground Floor" data-testid="emg-title-input" />
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1" placeholder="What happened? What's the current situation?" data-testid="emg-description" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-muted-foreground">CLIENT DETAILS</p>
                <div className="flex items-center gap-1 bg-muted rounded-md p-0.5" data-testid="client-mode-toggle">
                  <button type="button" onClick={() => { setClientMode("existing"); setForm(f => ({ ...f, client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "" })); setSelectedClientContacts([]); }}
                    className={`text-[10px] px-2.5 py-1 rounded font-medium transition-all ${clientMode === "existing" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="client-mode-existing">Select Existing</button>
                  <button type="button" onClick={() => { setClientMode("new"); setForm(f => ({ ...f, client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "" })); setSelectedClientContacts([]); }}
                    className={`text-[10px] px-2.5 py-1 rounded font-medium transition-all ${clientMode === "new" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="client-mode-new">Add New</button>
                </div>
              </div>
              {clientMode === "existing" ? (
                <div>
                  <Select onValueChange={selectClient} data-testid="client-select-wrapper">
                    <SelectTrigger className="w-full" data-testid="emg-client-select"><SelectValue placeholder="Choose a client..." /></SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.company_name ? `${c.company_name} — ` : ""}{[c.first_name, c.last_name].filter(Boolean).join(" ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.client_id && selectedClientContacts.length > 0 && (
                    <div className="mt-2">
                      <Label className="text-xs">Requester Contact</Label>
                      <Select value={form.requester_contact_id || "__none__"} onValueChange={selectRequester}>
                        <SelectTrigger className="mt-1" data-testid="emg-requester-select"><SelectValue placeholder="Tag a sub-contact as requester..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No specific requester</SelectItem>
                          {selectedClientContacts.map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full shrink-0 inline-block" style={{ backgroundColor: c.color || "#3b82f6" }} />
                                {c.first_name} {c.last_name}{c.job_role ? ` (${c.job_role})` : ""}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {form.client_name && (
                    <div className="mt-2 p-2.5 bg-muted/30 rounded-md border border-border text-xs space-y-1" data-testid="selected-client-preview">
                      <p className="font-semibold">{form.client_name} {form.client_company && <span className="text-muted-foreground font-normal">— {form.client_company}</span>}</p>
                      {form.client_email && <p className="text-muted-foreground flex items-center gap-1"><Envelope size={10} /> {form.client_email}</p>}
                      {form.client_phone && <p className="text-muted-foreground flex items-center gap-1"><Phone size={10} /> {form.client_phone}</p>}
                      {form.requester_contact_name && (
                        <p className="flex items-center gap-1.5 pt-1">
                          <span className="text-[10px] text-muted-foreground">Requester:</span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white" style={{ backgroundColor: form.requester_contact_color || "#3b82f6" }} data-testid="emg-requester-tag">{form.requester_contact_name}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} className="mt-1" data-testid="emg-client-name" /></div>
                  <div><Label>Email</Label><Input type="email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} className="mt-1" /></div>
                  <div><Label>Phone</Label><Input value={form.client_phone} onChange={e => setForm({ ...form, client_phone: e.target.value })} className="mt-1" /></div>
                  <div><Label>Company</Label><Input value={form.client_company} onChange={e => setForm({ ...form, client_company: e.target.value })} className="mt-1" /></div>
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">PROPERTY DETAILS</p>
              <div className="mb-3">
                <ClientPropertyPicker clientId={form.client_id} clientLabel={form.client_company || form.client_name} propertyId={form.property_id || ""} testid="emg-client-property"
                  onPick={(p) => setForm(f => p ? ({ ...f, property_id: p.id, property_name: p.name || f.property_name, street_address: p.address_line || "", town_city: p.town_city || "", county: p.county || "", postcode: p.postcode || "",
                    what3words: p.what3words || f.what3words, latitude: p.latitude ?? f.latitude, longitude: p.longitude ?? f.longitude,
                    site_contact_name: p.site_contact_name || f.site_contact_name, site_contact_phone: p.site_contact_phone || f.site_contact_phone, site_contact_email: p.site_contact_email || f.site_contact_email,
                    access_notes: p.access_notes || f.access_notes }) : ({ ...f, property_id: "" }))} />
              </div>
              <AddressAutocomplete
                label="Search address"
                placeholder="Start typing postcode or address..."
                onSelect={(a) => setForm(f => ({
                  ...f,
                  street_address: a.street_address || f.street_address,
                  town_city: a.town_city || f.town_city,
                  county: a.county || f.county,
                  postcode: a.postcode || f.postcode,
                  latitude: a.latitude || null,
                  longitude: a.longitude || null,
                }))}
                className="mb-3"
                data-testid="emg-address-autocomplete"
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Property Name</Label><Input value={form.property_name} onChange={e => setForm({ ...form, property_name: e.target.value })} placeholder="Optional" className="mt-1" /></div>
                <div className="col-span-2"><Label>Street Address *</Label><Input value={form.street_address} onChange={e => setForm({ ...form, street_address: e.target.value })} required className="mt-1" data-testid="emg-street" /></div>
                <div><Label>Town / City *</Label><Input value={form.town_city} onChange={e => setForm({ ...form, town_city: e.target.value })} required className="mt-1" data-testid="emg-town" /></div>
                <div><Label>County</Label><Input value={form.county} onChange={e => setForm({ ...form, county: e.target.value })} className="mt-1" /></div>
                <div><Label>Postcode *</Label><Input value={form.postcode} onChange={e => setForm({ ...form, postcode: e.target.value })} required className="mt-1" data-testid="emg-postcode" /></div>
                <div><Label>Category</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="mt-1" placeholder="e.g. Plumbing, Electrical" /></div>
                <div className="col-span-2"><Label>what3words <span className="text-[10px] text-muted-foreground">(optional)</span></Label><Input value={form.what3words} onChange={e => setForm({ ...form, what3words: e.target.value })} placeholder="///filled.count.soap" className="mt-1 font-mono" data-testid="emg-what3words" /></div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">SITE / PROPERTY CONTACT</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Site Contact</Label><Input value={form.site_contact_name} onChange={e => setForm({ ...form, site_contact_name: e.target.value })} placeholder="E.G Tenant" className="mt-1" data-testid="emg-site-contact-name" /></div>
                <div><Label>Phone</Label><Input value={form.site_contact_phone} onChange={e => setForm({ ...form, site_contact_phone: e.target.value })} placeholder="+44 7..." className="mt-1" data-testid="emg-site-contact-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={form.site_contact_email} onChange={e => setForm({ ...form, site_contact_email: e.target.value })} placeholder="tenant@email.com" className="mt-1" data-testid="emg-site-contact-email" /></div>
              </div>
            </div>
            <AccessNotesField value={form.access_notes || ""} onChange={(v) => setForm(f => ({ ...f, access_notes: v }))} propertyLinked={!!form.property_id} testid="emg-access-notes" />
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Assign Supplier</Label>
                <AiSmartAssignButton
                  category={form.category}
                  title={form.title}
                  description={form.description}
                  area={form.town_city || form.county}
                  onSelect={(rec) => setForm(prev => ({ ...prev, assigned_vendor_id: rec.vendor_id, assigned_vendor_name: rec.vendor_name }))}
                />
              </div>
              <div className="relative mt-1" ref={vendorSearchRef}>
                {form.assigned_vendor_id ? (
                  <div className="flex items-center justify-between border border-border rounded-md px-3 py-2 bg-muted/20" data-testid="selected-vendor-display">
                    <div className="flex items-center gap-2">
                      <Wrench size={14} className="text-muted-foreground" />
                      <span className="text-sm font-medium">{form.assigned_vendor_name}</span>
                      {(() => { const v = vendors.find(x => x.id === form.assigned_vendor_id); return v ? <span className="text-[10px] text-muted-foreground">— {v.trade || "General"}{Array.isArray(v.areas_covered) && v.areas_covered.length > 0 ? ` · ${v.areas_covered.slice(0, 2).join(", ")}` : ""}</span> : null; })()}
                    </div>
                    <button type="button" onClick={clearVendor} className="text-muted-foreground hover:text-foreground text-xs" data-testid="clear-vendor-btn">&times;</button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={vendorSearch}
                        onChange={e => { setVendorSearch(e.target.value); setVendorDropdownOpen(true); }}
                        onFocus={() => setVendorDropdownOpen(true)}
                        placeholder='Search supplier... e.g. "plumber in plymouth"'
                        className="pl-8 text-xs"
                        data-testid="vendor-search-input"
                      />
                    </div>
                    {vendorDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto border border-border rounded-md bg-popover shadow-lg" data-testid="vendor-search-dropdown">
                        {filteredVendors.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-4">No suppliers match your search</p>
                        ) : (
                          <>
                            <p className="text-[9px] text-muted-foreground px-3 py-1.5 border-b border-border bg-muted/30">{filteredVendors.length} supplier{filteredVendors.length !== 1 ? "s" : ""} found</p>
                            {filteredVendors.slice(0, 30).map(v => (
                              <button
                                key={v.id} type="button"
                                className="w-full text-left px-3 py-2 hover:bg-muted/40 transition-colors border-b border-border last:border-0"
                                onClick={() => selectVendor(v)}
                                data-testid={`vendor-option-${v.id}`}
                              >
                                <p className="text-xs font-semibold">{v.company_name || v.name}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {v.trade && <span className="text-[9px] text-muted-foreground flex items-center gap-0.5"><Wrench size={8} /> {v.trade}</span>}
                                  {Array.isArray(v.areas_covered) && v.areas_covered.length > 0 && (
                                    <span className="text-[9px] text-muted-foreground flex items-center gap-0.5"><MapPin size={8} /> {v.areas_covered.slice(0, 3).join(", ")}{v.areas_covered.length > 3 ? ` +${v.areas_covered.length - 3}` : ""}</span>
                                  )}
                                  {v.rating != null && (
                                    <span className="text-[9px] text-amber-600 font-medium">★ {v.rating}</span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div>
              <Label>Assign In-House Operative</Label>
              <Select value={form.assigned_operative_id || "none"} onValueChange={v => {
                if (v === "none") { setForm({ ...form, assigned_operative_id: "", assigned_operative_name: "" }); return; }
                const op = operatives.find(x => x._id === v);
                setForm({ ...form, assigned_operative_id: v, assigned_operative_name: op?.name || op?.email || "" });
              }}>
                <SelectTrigger className="mt-1" data-testid="emg-operative-select"><SelectValue placeholder="Select operative..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No operative assigned</SelectItem>
                  {operatives.length === 0 ? (
                    <div className="px-2 py-3 text-xs text-muted-foreground text-center">No in-house operatives yet. Add them in Team &rsaquo; In-House.</div>
                  ) : operatives.map(op => (
                    <SelectItem key={op._id} value={op._id} data-testid={`emg-operative-option-${op._id}`}>
                      {op.name || op.email}{op.trade ? ` — ${op.trade}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">Optionally assign an in-house team member alongside or instead of a supplier.</p>
            </div>
            <div>
              <Label>Special Instructions</Label>
              <Textarea value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })} rows={2} className="mt-1" placeholder="Anything else the engineer should know (access details go in the box above)..." data-testid="emg-instructions" />
            </div>
            <ScopeOfWorksField value={form.scope_of_works} onChange={(v) => setForm(f => ({ ...f, scope_of_works: v }))} testid="emg-scope-of-works" rows={4}
              context={{ entity: "emergency_job", title: form.title, description: form.description, client_name: form.client_company || form.client_name, property_address: [form.street_address, form.town_city, form.postcode].filter(Boolean).join(", ") }} />
            <NoticeEditor value={form.notice} onChange={(n) => setForm({ ...form, notice: n })} clientToggle aiContext={{ entity: "emergency_job", title: form.title, description: form.description }} testid="emg-notice" compact />
            <div>
              <JobFormsPicker value={form.job_form_ids} onChange={(ids) => setForm({ ...form, job_form_ids: ids })} testId="emg-job-forms-picker" />
            </div>
            <div>
              <Label>Supporting Images / Files</Label>
              <FileDropZone files={form.attachments} onFilesChange={files => setForm({ ...form, attachments: files })} folder="emergency" accept="all" maxFiles={10} className="mt-1" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-red-600 hover:bg-red-700 text-white" data-testid="create-emergency-submit">
                <Lightning size={14} className="mr-1" weight="fill" /> {creating ? "Creating..." : "Create Emergency Job"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
